import {
  assertFragmentAllocation,
  usesQuoteFragmentAllocation,
} from '../../../domain/quoteSourceFragment';
import type { CpqState, Quote, QuoteItem } from './types';

export type QuoteLocalPricing = NonNullable<
  NonNullable<QuoteItem['deviceFieldSnapshot']>['quoteLocalPricing']
>;

function safeCents(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
    ? value
    : undefined;
}

type LocalPricingCarrier = {
  deviceFieldSnapshot?: {
    quoteLocalPricing?: QuoteLocalPricing;
    [key: string]: unknown;
  };
};

export function quoteLocalPricing(
  item: LocalPricingCarrier,
): QuoteLocalPricing {
  return item.deviceFieldSnapshot?.quoteLocalPricing ?? {};
}

export function quoteLocalRentalPrice(item: QuoteItem): number | undefined {
  const cents = safeCents(quoteLocalPricing(item).rentalPriceCents);
  return cents === undefined
    ? (item.deviceFieldSnapshot?.rentalPrice ?? undefined)
    : cents / 100;
}

export function quoteLocalRetailPriceTax13(
  item: QuoteItem,
): number | undefined {
  const cents = safeCents(quoteLocalPricing(item).retailPriceTax13Cents);
  return cents === undefined
    ? (item.deviceFieldSnapshot?.retailPriceTax13 ?? undefined)
    : cents / 100;
}

export function quoteLocalDynamicCost(item: QuoteItem): number | undefined {
  const cents = safeCents(quoteLocalPricing(item).dynamicCostCents);
  const value =
    cents === undefined ? item.deviceFieldSnapshot?.dynamicCost : cents / 100;
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

export function quoteLocalDynamicCostLine(item: QuoteItem): number | undefined {
  const cents = safeCents(quoteLocalPricing(item).dynamicCostLineCents);
  const unit = quoteLocalDynamicCost(item);
  return cents === undefined
    ? unit === undefined
      ? undefined
      : unit * item.quantity
    : cents / 100;
}

export function quoteLocalExclusiveLineCents(
  item: LocalPricingCarrier,
): number | undefined {
  return safeCents(quoteLocalPricing(item).exclusiveLineCents);
}

/** The workbench discounted-price column always totals these exact line cents. */
export function quoteExclusiveLineCents(item: QuoteItem): number {
  if (item.sourceFragment && usesQuoteFragmentAllocation(item.sourceFragment))
    assertFragmentAllocation(
      item.fragmentAllocation,
      item.sourceFragment,
      item.quantity,
    );
  const local = quoteLocalExclusiveLineCents(item);
  if (local !== undefined) return local;
  if (item.sourceFragment && usesQuoteFragmentAllocation(item.sourceFragment)) {
    assertFragmentAllocation(
      item.fragmentAllocation,
      item.sourceFragment,
      item.quantity,
    );
    const cents = item.fragmentAllocation?.allocatedRentalLineCents;
    if (!Number.isSafeInteger(cents) || (cents ?? -1) < 0)
      throw new Error('分行缺少准确来源金额');
    return cents as number;
  }
  const quantity = item.quantity;
  if (item.sourceType === 'plan') {
    if (item.planProductSubtotalSnapshot !== undefined)
      return Math.round(item.planProductSubtotalSnapshot * 100);
    if (item.formalPlanOccurrence)
      return item.formalPlanOccurrence.sourceLineCents;
    return Math.round(
      (item.planProductPriceSnapshot ?? Number.NaN) * quantity * 100,
    );
  }
  if (
    item.sourceType === 'device' &&
    item.quoteGroupId &&
    item.packageItemPriceSnapshot !== undefined
  ) {
    return Math.round(item.packageItemPriceSnapshot * quantity * 100);
  }
  if (item.sourceType === 'package') {
    const relationQuantity = item.packageRelationQuantitySnapshot;
    if (
      item.packageItemMonthlySubtotalSnapshot !== undefined &&
      relationQuantity &&
      quantity % relationQuantity === 0
    )
      return (
        Math.round(item.packageItemMonthlySubtotalSnapshot * 100) *
        (quantity / relationQuantity)
      );
    return Math.round(
      (item.packageItemPriceSnapshot ?? Number.NaN) * quantity * 100,
    );
  }
  return Math.round(
    (quoteLocalRentalPrice(item) ?? Number.NaN) * quantity * 100,
  );
}

export function quoteExclusiveSubtotalCents(
  items: readonly QuoteItem[],
): number {
  let total = 0n;
  const maximum = BigInt(Number.MAX_SAFE_INTEGER);
  for (const item of items) {
    const cents = quoteExclusiveLineCents(item);
    // A missing source price remains unknown for display; do not turn it into
    // a table-wide exception or into a fabricated zero.
    if (Number.isNaN(cents)) return Number.NaN;
    if (!Number.isSafeInteger(cents) || cents < 0)
      throw new Error('专属金额合计超出安全分范围');
    total += BigInt(cents);
    if (total > maximum) throw new Error('专属金额合计超出安全分范围');
  }
  return Number(total);
}

export function quoteLocalExclusiveDiscount(
  item: QuoteItem,
  fallback: number,
): number;
export function quoteLocalExclusiveDiscount(
  item: QuoteItem,
  fallback: number | null,
): number | null;
export function quoteLocalExclusiveDiscount(
  item: QuoteItem,
  fallback: number | null,
): number | null {
  const raw = quoteLocalPricing(item).exclusiveDiscountPercent;
  if (raw === undefined || raw.trim() === '') return fallback;
  const value = Number(raw);
  // Legacy catalogue data can legitimately project a price above its current
  // basis. Preserve that quote fact instead of replacing >100% with a default.
  return Number.isFinite(value) && value >= 0 ? value : fallback;
}

/**
 * Derive one package-level fallback from frozen backend facts. This is only
 * used for historical snapshots that predate an explicit discount field.
 * Never derive one percentage per allocated row: cent rounding would make an
 * untouched package appear to contain several different discounts.
 */
export function frozenPackageDiscount(
  items: readonly QuoteItem[],
): number | undefined {
  if (
    items.some(
      (entry) =>
        typeof entry.deviceFieldSnapshot?.rentalPrice !== 'number' ||
        !Number.isFinite(entry.deviceFieldSnapshot.rentalPrice) ||
        typeof entry.packageItemPriceSnapshot !== 'number' ||
        !Number.isFinite(entry.packageItemPriceSnapshot),
    )
  )
    return undefined;
  const rentalBasisCents = items.reduce((sum, entry) => {
    const quantity = entry.packageRelationQuantitySnapshot ?? entry.quantity;
    return (
      sum +
      Math.round(
        (entry.deviceFieldSnapshot?.rentalPrice as number) * quantity * 100,
      )
    );
  }, 0);
  if (rentalBasisCents <= 0) return undefined;
  const packagePriceCents = items.reduce((sum, entry) => {
    const quantity = entry.packageRelationQuantitySnapshot ?? entry.quantity;
    return (
      sum +
      (entry.packageItemMonthlySubtotalSnapshot === undefined
        ? Math.round(
            (entry.packageItemPriceSnapshot ?? Number.NaN) * quantity * 100,
          )
        : Math.round(entry.packageItemMonthlySubtotalSnapshot * 100))
    );
  }, 0);
  return (packagePriceCents / rentalBasisCents) * 100;
}

/** Default package price compared with the workbench's current rental basis.
 * Old exclusive edits do not participate in the destination default. */
export function quotePackageDefaultDiscount(
  items: readonly QuoteItem[],
): number | undefined {
  return frozenPackageDiscount(
    items.map((item) =>
      item.deviceFieldSnapshot
        ? {
            ...item,
            deviceFieldSnapshot: {
              ...item.deviceFieldSnapshot,
              rentalPrice: quoteLocalRentalPrice(item),
            },
          }
        : item,
    ),
  );
}

export function withQuoteLocalPricing(
  item: QuoteItem,
  patch: Partial<QuoteLocalPricing>,
): QuoteItem {
  if (!item.deviceFieldSnapshot) throw new Error('报价行缺少设备冻结快照');
  const previous = { ...item.deviceFieldSnapshot.quoteLocalPricing };
  if (
    patch.exclusiveLineCents !== undefined &&
    patch.exclusiveLineBasisCents === undefined
  ) {
    delete previous.exclusiveLineBasisCents;
    delete previous.exclusiveLineBasisQuantity;
  }
  return {
    ...item,
    deviceFieldSnapshot: {
      ...item.deviceFieldSnapshot,
      quoteLocalPricing: {
        ...previous,
        ...patch,
      },
    },
  };
}

/** A source downgrade starts with the destination's default exclusive price.
 * Reference prices and editable costs remain quote-local facts. */
export function withoutQuoteExclusivePricing(item: QuoteItem): QuoteItem {
  const snapshot = item.deviceFieldSnapshot;
  if (!snapshot?.quoteLocalPricing) return item;
  const {
    exclusiveDiscountPercent: _discount,
    exclusiveLineCents: _line,
    exclusiveLineBasisCents: _basis,
    exclusiveLineBasisQuantity: _quantity,
    ...remaining
  } = snapshot.quoteLocalPricing;
  return {
    ...item,
    deviceFieldSnapshot: { ...snapshot, quoteLocalPricing: remaining },
  };
}

/** Keeps a whole-line edit proportional to source instances, without cent drift. */
export function resizeQuoteExclusiveLine(
  item: QuoteItem,
  previousSourceQuantity: number,
  nextSourceQuantity: number,
): QuoteItem {
  const local = quoteLocalPricing(item);
  if (local.exclusiveLineCents === undefined) return item;
  const basisCents =
    local.exclusiveLineBasisCents === undefined
      ? local.exclusiveLineCents
      : local.exclusiveLineBasisCents;
  // Existing snapshots store package counts, not total device quantities.
  const basisQuantity =
    local.exclusiveLineBasisQuantity === undefined
      ? previousSourceQuantity
      : local.exclusiveLineBasisQuantity;
  if (
    !Number.isSafeInteger(local.exclusiveLineCents) ||
    local.exclusiveLineCents < 0 ||
    !Number.isSafeInteger(basisCents) ||
    basisCents < 0 ||
    !Number.isSafeInteger(previousSourceQuantity) ||
    previousSourceQuantity < 1 ||
    !Number.isSafeInteger(nextSourceQuantity) ||
    nextSourceQuantity < 1 ||
    !Number.isSafeInteger(basisQuantity) ||
    basisQuantity < 1
  )
    throw new Error('专属金额或来源数量基准无效');
  const numerator = BigInt(basisCents) * BigInt(nextSourceQuantity);
  const denominator = BigInt(basisQuantity);
  const exclusiveLineCents = Number(
    (numerator * 2n + denominator) / (denominator * 2n),
  );
  if (!Number.isSafeInteger(exclusiveLineCents))
    throw new Error('专属金额超出安全范围');
  return withQuoteLocalPricing(item, {
    exclusiveLineCents,
    exclusiveLineBasisCents: basisCents,
    exclusiveLineBasisQuantity: basisQuantity,
  });
}

/** Preserve the exact frozen amount before a member edit creates a quote override. */
export function preserveQuoteMemberExclusivePricing(
  previous: QuoteItem,
  changed: QuoteItem,
  previousMemberQuantity: number,
  nextMemberQuantity: number,
  instanceQuantity: number,
  discount: number | null,
): QuoteItem {
  const local = quoteLocalPricing(previous);
  const hasDiscount = discount !== null && Number.isFinite(discount);
  if (local.exclusiveLineCents === undefined && !hasDiscount) return changed;
  const seeded =
    local.exclusiveLineCents === undefined
      ? withQuoteLocalPricing(changed, {
          exclusiveLineCents: quoteExclusiveLineCents(previous),
        })
      : changed;
  const resized = resizeQuoteExclusiveLineForMemberQuantity(
    seeded,
    previousMemberQuantity,
    nextMemberQuantity,
    instanceQuantity,
  );
  return hasDiscount
    ? withQuoteLocalPricing(resized, {
        exclusiveDiscountPercent: discount.toFixed(2),
      })
    : resized;
}

/** Scale a member's quote-local exclusive amount when its per-set quantity changes.
 * Keep its original instance-count basis so later package quantity edits still
 * use the same quote-workbench scaling rule.
 */
export function resizeQuoteExclusiveLineForMemberQuantity(
  item: QuoteItem,
  previousMemberQuantity: number,
  nextMemberQuantity: number,
  instanceQuantity: number,
): QuoteItem {
  const local = quoteLocalPricing(item);
  if (local.exclusiveLineCents === undefined) return item;
  if (
    !Number.isSafeInteger(previousMemberQuantity) ||
    previousMemberQuantity < 1 ||
    !Number.isSafeInteger(nextMemberQuantity) ||
    nextMemberQuantity < 1 ||
    !Number.isSafeInteger(instanceQuantity) ||
    instanceQuantity < 1
  )
    throw new Error('套餐成员数量基准无效');

  const previousLineQuantity = previousMemberQuantity * instanceQuantity;
  const nextLineQuantity = nextMemberQuantity * instanceQuantity;
  if (
    !Number.isSafeInteger(previousLineQuantity) ||
    !Number.isSafeInteger(nextLineQuantity)
  )
    throw new Error('套餐成员数量超出安全范围');

  const currentLine = resizeQuoteExclusiveLine(
    withQuoteLocalPricing(item, {
      exclusiveLineBasisCents: local.exclusiveLineCents,
      exclusiveLineBasisQuantity: previousLineQuantity,
    }),
    previousLineQuantity,
    nextLineQuantity,
  );
  const originalBasisCents =
    local.exclusiveLineBasisCents ?? local.exclusiveLineCents;
  const originalBasisQuantity =
    local.exclusiveLineBasisQuantity ?? instanceQuantity;
  const scaledBasis = resizeQuoteExclusiveLine(
    withQuoteLocalPricing(item, {
      exclusiveLineCents: originalBasisCents,
      exclusiveLineBasisCents: originalBasisCents,
      exclusiveLineBasisQuantity: previousMemberQuantity,
    }),
    previousMemberQuantity,
    nextMemberQuantity,
  );
  const nextBasisCents = quoteLocalPricing(scaledBasis).exclusiveLineCents;
  if (nextBasisCents === undefined) throw new Error('专属金额缩放失败');
  return withQuoteLocalPricing(currentLine, {
    exclusiveLineBasisCents: nextBasisCents,
    exclusiveLineBasisQuantity: originalBasisQuantity,
  });
}

/** Largest-remainder allocation keeps the edited group total exact to cents. */
export function allocateQuoteCents(
  totalCents: number,
  weights: readonly number[],
): number[] {
  if (!Number.isSafeInteger(totalCents) || totalCents < 0)
    throw new Error('折后价必须是大于等于 0 的分值');
  if (weights.length === 0) return [];
  const safeWeights = weights.map((value) =>
    Number.isFinite(value) && value > 0 ? value : 0,
  );
  const totalWeight = safeWeights.reduce((sum, value) => sum + value, 0);
  if (totalWeight === 0) {
    const base = Math.floor(totalCents / weights.length);
    return weights.map(
      (_, index) => base + (index < totalCents - base * weights.length ? 1 : 0),
    );
  }
  const exact = safeWeights.map(
    (weight) => (totalCents * weight) / totalWeight,
  );
  const allocated = exact.map(Math.floor);
  let remainder = totalCents - allocated.reduce((sum, value) => sum + value, 0);
  const order = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort(
      (left, right) =>
        right.fraction - left.fraction || left.index - right.index,
    );
  for (let index = 0; remainder > 0; index += 1, remainder -= 1)
    allocated[order[index % order.length].index] += 1;
  return allocated;
}

/** Match the API's integer half-up percentage conversion for quote totals. */
export function quotePercentageFromTotals(
  totalCents: number,
  basisCents: number,
): string | null {
  if (
    !Number.isSafeInteger(totalCents) ||
    totalCents < 0 ||
    !Number.isSafeInteger(basisCents) ||
    basisCents < 0
  )
    throw new Error('报价金额必须是非负整数分');
  if (basisCents === 0) return null;
  const total = BigInt(totalCents);
  const basis = BigInt(basisCents);
  const hundredths = (total * 10_000n + basis / 2n) / basis;
  return `${hundredths / 100n}.${String(hundredths % 100n).padStart(2, '0')}`;
}

/** Match the API's integer half-up price calculation from a two-place rate. */
export function quoteTotalFromPercentage(
  basisCents: number,
  percentage: number | string,
): number {
  if (!Number.isSafeInteger(basisCents) || basisCents < 0)
    throw new Error('计价基数必须是非负整数分');
  const decimal =
    typeof percentage === 'number' ? percentage.toFixed(2) : percentage;
  const match = /^(0|[1-9][0-9]*)(?:\.([0-9]{1,2}))?$/.exec(decimal);
  if (!match) throw new Error('折扣必须是非负数且最多两位小数');
  const scaled =
    BigInt(match[1] ?? '0') * 100n + BigInt((match[2] ?? '').padEnd(2, '0'));
  const result = (BigInt(basisCents) * scaled + 5_000n) / 10_000n;
  const cents = Number(result);
  if (!Number.isSafeInteger(cents)) throw new Error('金额超出精确处理范围');
  return cents;
}

export function calculateExclusiveLineAmounts(
  rows: readonly {
    baseCents: number;
    excludedFromExtraDiscount: boolean;
    baselineDiscountPercent: number | null;
    minimumLineCents?: number | null;
  }[],
  input: { discount?: number; lineCents?: number },
): { lineCents: number[]; discounts: string[] } {
  const bases = rows.map((row) => row.baseCents);
  if (bases.some((value) => !Number.isSafeInteger(value) || value < 0))
    throw new Error('设备租金或套餐价格待维护，暂不能调整专属折扣或分摊');
  const baseTotal = bases.reduce((sum, value) => sum + value, 0);
  if (!Number.isSafeInteger(baseTotal))
    throw new Error('计价基数超出精确金额范围，暂不能分摊');

  const fixedMinimums = new Map<number, number>();
  rows.forEach((row, index) => {
    if (!row.excludedFromExtraDiscount) return;
    if (row.minimumLineCents !== undefined) {
      const minimum = safeCents(row.minimumLineCents);
      if (minimum === undefined)
        throw new Error(
          '该组合套餐的目录价格基准未完整读取，暂不能调整专属价格',
        );
      fixedMinimums.set(index, minimum);
      return;
    }
    const baseline = row.baselineDiscountPercent;
    const base = bases[index];
    if (baseline === null || !Number.isFinite(baseline) || baseline < 0)
      throw new Error('该组合套餐的目录价格基准未完整读取，暂不能调整专属价格');
    if (base === undefined)
      throw new Error('设备租金或套餐价格待维护，暂不能调整专属折扣或分摊');
    fixedMinimums.set(index, quoteTotalFromPercentage(base, baseline));
  });

  // Use the same stable largest-remainder rule as allocateQuoteCents, with
  // integer products so even safe totals near MAX_SAFE_INTEGER stay exact.
  const allocate = (total: number, weights: readonly number[]): number[] => {
    const weightTotal = weights.reduce((sum, value) => sum + BigInt(value), 0n);
    if (weightTotal === 0n) {
      if (total > 0)
        throw new Error('设备租金或套餐价格待维护，暂不能调整专属折扣或分摊');
      return weights.map(() => 0);
    }
    const products = weights.map((weight) => BigInt(total) * BigInt(weight));
    const allocated = products.map((product) => Number(product / weightTotal));
    const allocatedTotal = allocated.reduce(
      (sum, value) => sum + BigInt(value),
      0n,
    );
    const remainder = Number(BigInt(total) - allocatedTotal);
    const order = products
      .map((product, index) => ({ index, fraction: product % weightTotal }))
      .sort((left, right) =>
        left.fraction === right.fraction
          ? left.index - right.index
          : left.fraction > right.fraction
            ? -1
            : 1,
      );
    for (let index = 0; index < remainder; index += 1)
      allocated[order[index].index] += 1;
    return allocated;
  };

  let lineCents: number[];
  const requestedDiscount = input.discount;
  if (requestedDiscount !== undefined) {
    if (!Number.isFinite(requestedDiscount) || requestedDiscount < 0)
      throw new Error('专属折扣必须是大于等于 0% 的有效数字');
    lineCents = bases.map((base, index) => {
      const requested = quoteTotalFromPercentage(
        base,
        requestedDiscount.toFixed(2),
      );
      const minimum = fixedMinimums.get(index);
      return minimum === undefined ? requested : Math.max(minimum, requested);
    });
  } else {
    const requestedTotal = input.lineCents ?? 0;
    const fixedTotal = [...fixedMinimums.values()].reduce(
      (sum, cents) => sum + cents,
      0,
    );
    if (!Number.isSafeInteger(fixedTotal))
      throw new Error('报价分摊金额超出精确处理范围');
    if (!Number.isSafeInteger(requestedTotal) || requestedTotal < fixedTotal)
      throw new Error('该套餐组合不参与额外折扣，折后金额不能低于目录套餐价');
    const eligibleIndexes = bases.flatMap((_, index) =>
      fixedMinimums.has(index) ? [] : [index],
    );
    if (fixedMinimums.size === 0) {
      // Preserve the ordinary zero-basis equal split. Fixed-price floors use
      // the stricter allocator below because extra amounts need known weights.
      lineCents =
        baseTotal === 0
          ? allocateQuoteCents(requestedTotal, bases)
          : allocate(requestedTotal, bases);
    } else if (eligibleIndexes.length === 0) {
      lineCents =
        baseTotal === 0
          ? allocate(requestedTotal - fixedTotal, bases).map(
              (extra, index) => extra + (fixedMinimums.get(index) ?? 0),
            )
          : allocate(requestedTotal, bases);
      if (
        lineCents.some(
          (cents, index) => cents < (fixedMinimums.get(index) ?? 0),
        )
      ) {
        // Preserve the existing proportional result whenever every floor is
        // met; otherwise allocate only the extra above each frozen minimum.
        const extras = allocate(requestedTotal - fixedTotal, bases);
        lineCents = extras.map(
          (extra, index) => extra + (fixedMinimums.get(index) ?? 0),
        );
      }
    } else {
      const eligibleBases: number[] = [];
      for (const index of eligibleIndexes) {
        const base = bases[index];
        if (base === undefined)
          throw new Error('设备租金或套餐价格待维护，暂不能调整专属折扣或分摊');
        eligibleBases.push(base);
      }
      const eligibleAllocation = allocate(
        requestedTotal - fixedTotal,
        eligibleBases,
      );
      lineCents = bases.map((_, index) => fixedMinimums.get(index) ?? 0);
      eligibleIndexes.forEach((index, offset) => {
        const allocation = eligibleAllocation[offset];
        if (allocation === undefined)
          throw new Error('报价分摊金额超出精确处理范围');
        lineCents[index] = allocation;
      });
    }
  }

  if (lineCents.some((value) => !Number.isSafeInteger(value) || value < 0))
    throw new Error('报价分摊金额超出精确处理范围');
  const total = lineCents.reduce((sum, cents) => sum + BigInt(cents), 0n);
  if (
    total > BigInt(Number.MAX_SAFE_INTEGER) ||
    (requestedDiscount === undefined &&
      total !== BigInt(input.lineCents ?? 0)) ||
    lineCents.some((cents, index) => cents < (fixedMinimums.get(index) ?? 0))
  )
    throw new Error('报价分摊金额超出精确处理范围');
  const discounts = lineCents.map((cents, index) =>
    bases[index] === 0 || bases[index] === undefined
      ? '0.00'
      : (quotePercentageFromTotals(cents, bases[index]) ?? '0.00'),
  );
  if (discounts.some((discount) => !Number.isFinite(Number(discount))))
    throw new Error('专属折扣超出精确处理范围');
  return { lineCents, discounts };
}

/** Freeze both fields before changing a complete source to quote-customized.
 * The percentage describes its existing display basis; it never reprices the
 * exact line cents. Local recipes retain their established 100% default. */
export function freezeQuoteContainerPricing(
  item: QuoteItem,
  quote: Quote,
  catalog?: CpqState['catalog'],
): QuoteItem {
  const local = quoteLocalPricing(item);
  const source = quote.items.find((row) => row.itemId === item.itemId) ?? item;
  const leaf = quote.containerInstances?.find(
    (instance) =>
      instance.id === item.containerInstanceId && instance.kind === 'package',
  );
  const parent = leaf?.parentInstanceId
    ? quote.containerInstances?.find(
        (instance) =>
          instance.id === leaf.parentInstanceId && instance.kind === 'plan',
      )
    : undefined;
  const percent = (value: unknown): number | undefined => {
    if (value === undefined || value === null) return undefined;
    if (
      (typeof value !== 'string' && typeof value !== 'number') ||
      String(value).trim() === ''
    )
      throw new Error('套餐专属折扣来源无效，未转换报价来源');
    const result = Number(value);
    if (!Number.isFinite(result) || result < 0)
      throw new Error('套餐专属折扣来源无效，未转换报价来源');
    return result;
  };
  let discount =
    percent(local.exclusiveDiscountPercent) ??
    percent(quoteLocalPricing(source).exclusiveDiscountPercent) ??
    percent(item.fragmentAllocation?.sourceExclusiveDiscountPercent) ??
    percent(source.fragmentAllocation?.sourceExclusiveDiscountPercent) ??
    percent(parent?.planDiscountSnapshot) ??
    percent(source.planDiscountSnapshot) ??
    (source.formalPlanOccurrence
      ? percent(Number(source.formalPlanOccurrence.pricingCoefficient) * 100)
      : undefined);
  const group = quote.quoteGroups?.find(
    (candidate) => candidate.id === (source.quoteGroupId ?? item.quoteGroupId),
  );
  const rows = quote.items.filter((row) =>
    item.containerInstanceId
      ? row.containerInstanceId === item.containerInstanceId
      : source.quoteGroupId
        ? row.quoteGroupId === source.quoteGroupId
        : row.sourceType === source.sourceType &&
          row.sourceId === source.sourceId &&
          row.sourceRevisionId === source.sourceRevisionId,
  );
  if (
    discount === undefined &&
    (leaf?.originKind === 'quote-local-package' ||
      group?.kind === 'quote-local-package')
  )
    discount = 100;
  if (discount === undefined && source.sourceType === 'plan') {
    const members = rows.length ? rows : [source];
    const basis = members.reduce(
      (sum, row) =>
        sum +
        (row.packageItemMonthlySubtotalSnapshot !== undefined &&
        row.packageRelationQuantitySnapshot
          ? (Math.round(row.packageItemMonthlySubtotalSnapshot * 100) *
              row.quantity) /
            row.packageRelationQuantitySnapshot
          : Math.round(
              (row.packageItemPriceSnapshot ?? Number.NaN) * row.quantity * 100,
            )),
      0,
    );
    const total = members.reduce(
      (sum, row) => sum + quoteExclusiveLineCents(row),
      0,
    );
    if (
      Number.isSafeInteger(basis) &&
      basis > 0 &&
      Number.isSafeInteger(total) &&
      total >= 0
    )
      discount = percent((total / basis) * 100);
  }
  if (
    discount === undefined &&
    (source.sourceType === 'package' || leaf?.originKind === 'catalog-package')
  ) {
    const id = leaf?.originId ?? source.packageId ?? source.sourceId;
    const revision =
      leaf?.originRevisionId ??
      source.packageRevisionId ??
      source.sourceRevisionId;
    const pkg = catalog?.packages.find(
      (candidate) =>
        candidate.id === id && candidate.currentRevisionId === revision,
    );
    discount =
      percent(pkg?.packageDiscountPercent) ??
      percent(quotePackageDefaultDiscount(rows.length ? rows : [source]));
  }
  if (discount === undefined)
    throw new Error('套餐专属折扣来源尚未维护，未转换报价来源');
  const line = local.exclusiveLineCents ?? quoteExclusiveLineCents(item);
  if (!Number.isSafeInteger(line) || line < 0)
    throw new Error('套餐专属金额来源无效，未转换报价来源');
  const count =
    local.exclusiveLineBasisQuantity ??
    item.sourceFragment?.instanceQuantity ??
    leaf?.quantity;
  if (!count || !Number.isSafeInteger(count) || count < 1)
    throw new Error('套餐份数来源不完整，未转换报价来源');
  return withQuoteLocalPricing(item, {
    exclusiveDiscountPercent: discount.toFixed(2),
    exclusiveLineCents: line,
    exclusiveLineBasisCents: local.exclusiveLineBasisCents ?? line,
    exclusiveLineBasisQuantity: count,
  });
}
